const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
export function isIsoDate(value) {
    if (typeof value !== "string")
        return false;
    const match = ISO_DATE.exec(value);
    if (!match)
        return false;
    const [, y, m, d] = match.map(Number);
    const date = new Date(Date.UTC(y, m - 1, d));
    return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
}
function parts(iso) {
    if (!isIsoDate(iso))
        throw new Error(`invalid_date:${iso}`);
    const [y, m, d] = iso.split("-").map(Number);
    return [y, m, d];
}
function toIso(y, m, d) {
    return `${String(y).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}
/** Soma meses mantendo o dia; se o mês de destino é mais curto, usa o último dia (31/01 + 1 mês = 28/02). */
export function addMonths(iso, months) {
    const [y, m, d] = parts(iso);
    const index = y * 12 + (m - 1) + months;
    const year = Math.floor(index / 12);
    const month = (index % 12) + 1;
    const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
    return toIso(year, month, Math.min(d, lastDay));
}
export function addDays(iso, days) {
    const [y, m, d] = parts(iso);
    const date = new Date(Date.UTC(y, m - 1, d + days));
    return toIso(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
}
/** Diferença `b - a` em dias de calendário. */
export function diffDays(a, b) {
    const [ay, am, ad] = parts(a);
    const [by, bm, bd] = parts(b);
    return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86_400_000);
}
/**
 * Divide um valor em `count` parcelas mensais. A soma das parcelas é SEMPRE o total: o resto dos
 * centavos vai para a última parcela (R$ 100,00 em 3x = 33,33 + 33,33 + 33,34).
 */
export function splitInstallments(totalCents, count, firstDueDate, intervalMonths = 1) {
    if (!Number.isInteger(totalCents) || totalCents <= 0)
        throw new Error("invalid_total");
    if (!Number.isInteger(count) || count < 1 || count > 120)
        throw new Error("invalid_installment_count");
    if (!Number.isInteger(intervalMonths) || intervalMonths < 1 || intervalMonths > 12)
        throw new Error("invalid_interval");
    if (count > totalCents)
        throw new Error("installments_exceed_cents");
    parts(firstDueDate);
    const base = Math.floor(totalCents / count);
    return Array.from({ length: count }, (_, index) => ({
        installmentNo: index + 1,
        installmentsTotal: count,
        amountCents: index === count - 1 ? totalCents - base * (count - 1) : base,
        dueDate: addMonths(firstDueDate, index * intervalMonths)
    }));
}
export function isOverdue(item, today) {
    return item.status === "open" && item.dueDate < today;
}
function movementsOf(input) {
    const rows = [];
    for (const receivable of input.receivables) {
        if (receivable.status === "cancelled")
            continue;
        if (receivable.status === "received") {
            rows.push({ date: (receivable.receivedAt ?? receivable.dueDate).slice(0, 10), inCents: receivable.receivedAmountCents ?? receivable.amountCents, outCents: 0, pending: false });
        }
        else {
            rows.push({ date: receivable.dueDate < input.today ? input.today : receivable.dueDate, inCents: receivable.amountCents, outCents: 0, pending: true });
        }
    }
    for (const payable of input.payables) {
        if (payable.status === "cancelled")
            continue;
        if (payable.status === "paid") {
            rows.push({ date: (payable.paidAt ?? payable.dueDate).slice(0, 10), inCents: 0, outCents: payable.paidAmountCents ?? payable.amountCents, pending: false });
        }
        else {
            rows.push({ date: payable.dueDate < input.today ? input.today : payable.dueDate, inCents: 0, outCents: payable.amountCents, pending: true });
        }
    }
    return rows;
}
/** Primeiro dia (segunda-feira) da semana que contém `iso`. */
function weekStart(iso) {
    const [y, m, d] = parts(iso);
    const weekday = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0 = domingo
    return addDays(iso, -((weekday + 6) % 7));
}
function bucketBounds(date, granularity) {
    if (granularity === "day")
        return { key: date, start: date, end: date };
    if (granularity === "week") {
        const start = weekStart(date);
        return { key: start, start, end: addDays(start, 6) };
    }
    const [y, m] = parts(date);
    const start = toIso(y, m, 1);
    return { key: start.slice(0, 7), start, end: addDays(addMonths(start, 1), -1) };
}
export function buildCashflow(input) {
    parts(input.from);
    parts(input.to);
    parts(input.openingDate);
    parts(input.today);
    if (input.to < input.from)
        throw new Error("invalid_range");
    const movements = movementsOf(input);
    const sum = (rows, pick) => rows.reduce((acc, row) => acc + pick(row), 0);
    const before = movements.filter((m) => m.date >= input.openingDate && m.date < input.from);
    const startingBalanceCents = input.openingBalanceCents + sum(before, (m) => m.inCents) - sum(before, (m) => m.outCents);
    // Todos os períodos entre `from` e `to`, mesmo os sem movimento (o saldo continua no gráfico).
    const buckets = [];
    let cursor = input.from;
    let balance = startingBalanceCents;
    while (cursor <= input.to) {
        const bounds = bucketBounds(cursor, input.granularity);
        const inside = movements.filter((m) => m.date >= bounds.start && m.date <= bounds.end && m.date >= input.openingDate && m.date >= input.from && m.date <= input.to);
        const inCents = sum(inside, (m) => m.inCents);
        const outCents = sum(inside, (m) => m.outCents);
        balance += inCents - outCents;
        buckets.push({
            key: bounds.key,
            startDate: bounds.start,
            endDate: bounds.end,
            inCents,
            outCents,
            inPendingCents: sum(inside.filter((m) => m.pending), (m) => m.inCents),
            outPendingCents: sum(inside.filter((m) => m.pending), (m) => m.outCents),
            netCents: inCents - outCents,
            balanceCents: balance
        });
        cursor = addDays(bounds.end, 1);
    }
    const overdueReceivables = input.receivables.filter((r) => isOverdue({ status: r.status, dueDate: r.dueDate }, input.today));
    const overduePayables = input.payables.filter((p) => isOverdue({ status: p.status, dueDate: p.dueDate }, input.today));
    const totalIn = buckets.reduce((acc, b) => acc + b.inCents, 0);
    const totalOut = buckets.reduce((acc, b) => acc + b.outCents, 0);
    const lowest = buckets.reduce((min, b) => (min === null || b.balanceCents < min.balanceCents ? b : min), null);
    return {
        buckets,
        totals: { inCents: totalIn, outCents: totalOut, netCents: totalIn - totalOut },
        overdue: {
            receivableCents: overdueReceivables.reduce((acc, r) => acc + r.amountCents, 0),
            receivableCount: overdueReceivables.length,
            payableCents: overduePayables.reduce((acc, p) => acc + p.amountCents, 0),
            payableCount: overduePayables.length
        },
        startingBalanceCents,
        endingBalanceCents: balance,
        lowestBalance: lowest ? { cents: lowest.balanceCents, bucketKey: lowest.key } : null
    };
}
